import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { useBasicTicketCost } from '@mopla/business-logic';
import { MplBottomSheet, MplButton } from '@mopla/ui';
import { formatPrice } from '@mopla/utils';

interface ISubscribeDTicketBottomSheetProps {
	priceToPay?: number;
	fullPrice?: number;
	buttonLabel: string;
	onButtonClick: () => void;
	isDisabled?: boolean;
	isLoading?: boolean;
}

export const SubscribeDTicketBottomSheet: FC<
	ISubscribeDTicketBottomSheetProps
> = ({
	priceToPay,
	fullPrice,
	buttonLabel,
	onButtonClick,
	isDisabled = false,
	isLoading = false,
}) => {
	const { t } = useTranslation('subscribeDTicket');
	const { basicTicketCost } = useBasicTicketCost();

	const displayPrice = priceToPay ?? basicTicketCost;
	const displayFullPrice = fullPrice ?? basicTicketCost;
	const hasDiscount = displayPrice < displayFullPrice;

	return (
		<MplBottomSheet open isStatic testId="subscribe-dticket-bottom-sheet">
			<Stack gap={2} sx={{ pt: 2, px: 2 }}>
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
				>
					<Typography variant="h4-sb" color="primary.main">
						{t('bottom_sheet.price_label')}
					</Typography>

					<Stack alignItems="flex-end">
						<Stack direction="row" gap={0.5} alignItems="baseline">
							{hasDiscount && (
								<Typography
									variant="b1-m"
									color="solutions.600"
									sx={{ textDecoration: 'line-through' }}
								>
									{t('bottom_sheet.full_price_per_month', {
										price: formatPrice(displayFullPrice),
									})}
								</Typography>
							)}
							<Typography variant="b1-m" color="primary.main">
								{t('bottom_sheet.price_per_month', {
									price: formatPrice(displayPrice),
								})}
							</Typography>
						</Stack>
						<Typography variant="l2" color="solutions.600">
							{t('bottom_sheet.including_vat')}
						</Typography>
					</Stack>
				</Stack>

				<MplButton
					variant="contained"
					color="primary"
					fullWidth
					onClick={onButtonClick}
					disabled={isDisabled}
					showLoader={isLoading}
					testId="subscribe-dticket-next-button"
				>
					{buttonLabel}
				</MplButton>
			</Stack>
		</MplBottomSheet>
	);
};
